class ProductScentSelector extends HTMLElement {
    connectedCallback() {
        this.trigger = this.querySelector('[data-scent-selector-trigger]');
        this.panel = this.querySelector('[data-scent-selector-panel]');
        this.overlay = this.querySelector('[data-scent-selector-overlay]');
        this.closeButton = this.querySelector('[data-scent-selector-close]');
        this.optionsContainer = this.querySelector('.scent-selector__options');
        this.currentItem = this.querySelector('.scent-selector__current');
        this.currentLabelValue = this.querySelector('.scent-selector__label span');

        this.isOpen = false;
        this.previousBodyOverflow = null;

        if (!this.trigger || !this.panel) return;

        this.onTriggerClick = this.onTriggerClick.bind(this);
        this.onOverlayClick = this.onOverlayClick.bind(this);
        this.onCloseClick = this.onCloseClick.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);
        this.onOutsideClick = this.onOutsideClick.bind(this);
        this.onOptionClick = this.onOptionClick.bind(this);

        this.trigger.addEventListener('click', this.onTriggerClick);
        if (this.overlay) this.overlay.addEventListener('click', this.onOverlayClick);
        if (this.closeButton) this.closeButton.addEventListener('click', this.onCloseClick);
        if (this.optionsContainer) this.optionsContainer.addEventListener('click', this.onOptionClick);
    }

    disconnectedCallback() {
        if (this.trigger) this.trigger.removeEventListener('click', this.onTriggerClick);
        if (this.overlay) this.overlay.removeEventListener('click', this.onOverlayClick);
        if (this.closeButton) this.closeButton.removeEventListener('click', this.onCloseClick);
        if (this.optionsContainer) this.optionsContainer.removeEventListener('click', this.onOptionClick);
        document.removeEventListener('keydown', this.onKeyDown);
        document.removeEventListener('click', this.onOutsideClick, true);
        this.unlockScroll();
        document.body.classList.remove('scent-selector--open-mobile');
    }

    isMobile() {
        return window.matchMedia && window.matchMedia(`(max-width: 767px)`).matches;
    }

    lockScroll() {
        if (!this.isMobile()) return;
        if (this.previousBodyOverflow !== null) return;
        this.previousBodyOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
    }

    unlockScroll() {
        if (this.previousBodyOverflow === null) return;
        document.body.style.overflow = this.previousBodyOverflow;
        this.previousBodyOverflow = null;
    }

    open() {
        if (this.isOpen) return;
        this.isOpen = true;
        this.classList.add('is-open');
        this.trigger.setAttribute('aria-expanded', 'true');

        this.panel.hidden = false;
        if (this.isMobile() && this.overlay) this.overlay.hidden = false;
        if (this.isMobile()) document.body.classList.add('scent-selector--open-mobile');

        this.lockScroll();

        document.addEventListener('keydown', this.onKeyDown);
        document.addEventListener('click', this.onOutsideClick, true);
    }

    close() {
        if (!this.isOpen) return;
        this.isOpen = false;
        this.classList.remove('is-open');
        this.trigger.setAttribute('aria-expanded', 'false');

        this.panel.hidden = true;
        if (this.overlay) this.overlay.hidden = true;
        document.body.classList.remove('scent-selector--open-mobile');

        document.removeEventListener('keydown', this.onKeyDown);
        document.removeEventListener('click', this.onOutsideClick, true);

        this.unlockScroll();
    }

    onTriggerClick(event) {
        event.preventDefault();
        this.isOpen ? this.close() : this.open();
    }

    onOverlayClick(event) {
        event.preventDefault();
        this.close();
    }

    onCloseClick(event) {
        event.preventDefault();
        this.close();
    }

    onKeyDown(event) {
        if (event.key === 'Escape') this.close();
    }

    onOutsideClick(event) {
        if (!this.isOpen) return;
        if (this.isMobile()) return;
        if (this.contains(event.target)) return;
        this.close();
    }

    onOptionClick(event) {
        const option = event.target.closest('[data-scent-selector-option]');
        if (!option) return;

        if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
        event.preventDefault();

        this.applySelectionFromOption(option);
        this.close();

        const targetUrl = option.getAttribute('href');
        if (!targetUrl) return;

        this.refreshMainProductSection(targetUrl);
    }

    applySelectionFromOption(option) {
        const title = option.querySelector('.title')?.textContent?.trim();
        if (title && this.currentLabelValue) this.currentLabelValue.textContent = title;

        if (this.currentItem) {
            const fragrance = option.getAttribute('data-fragrance');
            if (fragrance) this.currentItem.setAttribute('data-fragrance', fragrance);

            ['title', 'description', 'notes', 'tags'].forEach((klass) => {
                const from = option.querySelector(`.${klass}`);
                const to = this.currentItem.querySelector(`.${klass}`);
                if (from && to) to.innerHTML = from.innerHTML;
            });
        }

        this.querySelectorAll('[data-scent-selector-option].is-current').forEach((el) => {
            el.classList.remove('is-current');
            el.removeAttribute('aria-current');
        });
        option.classList.add('is-current');
        option.setAttribute('aria-current', 'page');
    }

    getSectionId() {
        const sectionEl = this.closest('[id^="shopify-section-"]');
        if (!sectionEl) return null;
        return sectionEl.id.replace('shopify-section-', '');
    }

    getHandleFromUrl(url) {
        try {
            const parsed = new URL(url, window.location.origin);
            const parts = parsed.pathname.split('/').filter(Boolean);
            const productsIndex = parts.indexOf('products');
            if (productsIndex === -1) return null;
            return parts[productsIndex + 1] || null;
        } catch (_) {
            return null;
        }
    }

    async refreshMainProductSection(url) {
        const baseHandle = this.getHandleFromUrl(url);
        const sectionId = this.getSectionId();
        if (!baseHandle || !sectionId) {
            window.location.assign(url);
            return;
        }

        const sizeState = this.getCurrentSizeState(sectionId);

        try {
            const html = await this.fetchSectionHtml(baseHandle, sectionId);

            const selection = this.resolveGroupedSizeSelectionFromHtml(html, sectionId, sizeState);

            const handleForRequest = selection?.handle || baseHandle;
            const productIdForRequest = selection?.productId || null;

            const variantGroups = this.getVariantGroups(sectionId);
            if (!variantGroups) {
                window.location.assign(`${window.routes.root_url}products/${handleForRequest}`);
                return;
            }

            const incomingVariantGroups = html.querySelector(`#shopify-section-${sectionId} variant-groups`);
            if (!incomingVariantGroups) {
                window.location.assign(`${window.routes.root_url}products/${handleForRequest}`);
                return;
            }

            // Swap the product-group UI so subsequent size changes target the new scent group.
            // We replace the whole custom element (native sizes behavior expects the embedded JSON).
            variantGroups.replaceWith(incomingVariantGroups);
            try {
                if (window.customElements?.upgrade) window.customElements.upgrade(incomingVariantGroups);
            } catch (_) {}

            if (typeof incomingVariantGroups.refreshControls === 'function') incomingVariantGroups.refreshControls();
            else {
                incomingVariantGroups.radios = incomingVariantGroups.querySelectorAll('.item input[type="radio"][name="product"]');
                incomingVariantGroups.rcRadios = incomingVariantGroups.querySelectorAll('.item input[type="radio"].rc_radio');
                incomingVariantGroups.rcSelects = incomingVariantGroups.querySelectorAll('.item .rc_select__frequency');
            }
            if (typeof incomingVariantGroups.initialize === 'function') incomingVariantGroups.initialize();

            // Ensure we have a checked product radio before letting VariantGroups run.
            const radioToSelect = productIdForRequest
                ? incomingVariantGroups.querySelector(`input[type="radio"][name="product"][value="${productIdForRequest}"]`)
                : null;

            if (!radioToSelect) {
                const firstRadio = incomingVariantGroups.querySelector(`input[type="radio"][name="product"]`);
                if (!firstRadio) {
                    window.location.assign(`${window.routes.root_url}products/${handleForRequest}`);
                    return;
                }
                firstRadio.checked = true;
                firstRadio.dispatchEvent(new Event('change', { bubbles: true }));
                return;
            }

            radioToSelect.checked = true;
            radioToSelect.dispatchEvent(new Event('change', { bubbles: true }));
        } catch (_) {
            window.location.assign(url);
        }
    }

    getCurrentSizeState(sectionId) {
        return {
            groupSizeLabel: this.getSelectedGroupSizeLabel(sectionId),
            groupSizeIndex: this.getSelectedGroupSizeIndex(sectionId),
        };
    }

    getSelectedGroupSizeIndex(sectionId) {
        const root = document.getElementById(`shopify-section-${sectionId}`);
        const group = root?.querySelector('variant-groups.card-layout');
        if (!group) return null;
        const items = Array.from(group.querySelectorAll('.item'));
        if (items.length === 0) return null;
        const index = items.findIndex((item) => item.querySelector('input[type="radio"][name="product"]')?.checked);
        return index >= 0 ? index : null;
    }

    getSelectedGroupSizeLabel(sectionId) {
        const root = document.getElementById(`shopify-section-${sectionId}`);
        const group = root?.querySelector('variant-groups.card-layout');
        if (!group) return null;
        const checked = group.querySelector('input[type="radio"][name="product"]:checked');
        if (!checked) return null;
        const label = group.querySelector(`label[for="${checked.id}"]`);
        if (!label) return null;
        const span = Array.from(label.querySelectorAll('span')).find((s) => s.classList.length === 0);
        return span ? span.textContent.trim() : null;
    }

    async fetchSectionHtml(handle, sectionId) {
        const url = `${window.routes.root_url}products/${handle}?section_id=${sectionId}`;
        const response = await fetch(url);
        const responseText = await response.text();
        return new DOMParser().parseFromString(responseText, 'text/html');
    }

    resolveGroupedSizeSelectionFromHtml(html, sectionId, sizeState) {
        const scope = `#shopify-section-${sectionId}`;
        const group = html.querySelector(`${scope} variant-groups.card-layout`);
        if (!group) return null;

        const items = Array.from(group.querySelectorAll('.item'));
        if (items.length === 0) return null;

        let matchItem = null;

        if (sizeState.groupSizeLabel) {
            const wanted = sizeState.groupSizeLabel.trim().toLowerCase();
            matchItem = items.find((item) => {
                const label = item.querySelector('label');
                if (!label) return false;
                const span = Array.from(label.querySelectorAll('span')).find((s) => s.classList.length === 0);
                const value = span ? span.textContent.trim().toLowerCase() : '';
                return value === wanted;
            });
        }

        if (!matchItem && sizeState.groupSizeIndex !== null && sizeState.groupSizeIndex >= 0) {
            matchItem = items[sizeState.groupSizeIndex] || null;
        }

        if (!matchItem) return null;

        const jsonScript = matchItem.querySelector('script[type="application/json"][id^="json_"]');
        if (!jsonScript) return null;
        try {
            const productData = JSON.parse(jsonScript.textContent);
            return {
                handle: productData?.handle || null,
                productId: productData?.id || null,
            };
        } catch (_) {
            return null;
        }
    }

    getVariantGroups(sectionId) {
        return document.querySelector(`#shopify-section-${sectionId} variant-groups`);
    }
}

if (!customElements.get('product-scent-selector')) {
    customElements.define('product-scent-selector', ProductScentSelector);
}
